import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { IconButton } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import type JawSegmentation from '@segmentation/three/JawSegmentation';
import { GumTrimTool } from '@segmentation/three/GumTrimTool';
import { gumEditStore } from '@segmentation/store/gumEditStore';
import { toolManager } from '@modules/three/tools/ToolManager';
import { threeInteractionStore } from '@modules/three/store/threeInteractionStore';
import { threeStore } from '@modules/three/store/threeStore';
import { InteractiveSplineToolEnum } from '@/modules/three/common/curve/interactiveSpline/InteractiveSplineToolEnum';

interface JawReviewPanelProps {
  jaw: 'upper' | 'lower';
  jawMesh: JawSegmentation;
  onBackToRefine: () => void;
  onContinue: () => void;
  isContinuePending?: boolean;
}

export function JawReviewPanel({
  jaw,
  jawMesh,
  onBackToRefine,
  onContinue,
  isContinuePending = false,
}: JawReviewPanelProps) {
  const jawLabel = jaw === 'lower' ? 'lower' : 'upper';
  const isLower = jaw === 'lower';
  const trimMargin = gumEditStore((s) => s.trimMargin);
  const setTrimMargin = gumEditStore((s) => s.setTrimMargin);
  const busy = gumEditStore((s) => s.busy);
  const setBusy = gumEditStore((s) => s.setBusy);
  const [trimBounds, setTrimBounds] = useState<{ min: number; max: number } | null>(null);
  const [fineTune, setFineTune] = useState(false);

  // Trim tool lifecycle: distance field + drag interaction for the review step.
  useEffect(() => {
    const bounds = jawMesh.prepareGumTrimByTeeth();
    setTrimBounds(bounds);
    if (!bounds) return;
    const tool = new GumTrimTool({
      getJaw: () => jawMesh,
      setControlsEnabled: (enabled) => {
        const controls = threeStore.getState().rootState?.controls;
        if (controls) controls.enabled = enabled;
      },
    });
    toolManager.register(tool);
    threeInteractionStore.getState().setActiveTool(tool.name);
    return () => {
      threeInteractionStore.getState().setActiveTool(null);
      toolManager.unregister(tool.name);
      jawMesh.disposeGumTrim();
      gumEditStore.getState().setTrimMargin(null);
    };
  }, [jawMesh]);

  // Fine-tune mode: recompute the local margins after each control-point drag
  // (DraggingHandler listens on document, so wait a tick for it to finish).
  useEffect(() => {
    if (!fineTune) return;
    const onPointerUp = () => {
      globalThis.setTimeout(() => jawMesh.updateGumTrimFromSpline(), 25);
    };
    document.addEventListener('pointerup', onPointerUp);
    return () => document.removeEventListener('pointerup', onPointerUp);
  }, [fineTune, jawMesh]);

  const handleMarginSlider = (value: number) => {
    setTrimMargin(value);
    jawMesh.previewGumTrimByTeeth(value);
  };

  const handleStartFineTune = () => {
    if (trimMargin === null) return;
    if (!jawMesh.startGumTrimSplineEdit(trimMargin)) {
      toast.error('Could not build the cut line spline');
      return;
    }
    // Control points need the default raycast path — release the trim tool.
    threeInteractionStore.getState().setActiveTool(null);
    threeInteractionStore.getState().setEditableLineTool(InteractiveSplineToolEnum.Dragging);
    setFineTune(true);
  };

  const handleStopFineTune = () => {
    jawMesh.disposeGumTrimSpline();
    threeInteractionStore.getState().setActiveTool('gum-trim');
    if (trimMargin !== null) jawMesh.previewGumTrimByTeeth(trimMargin);
    setFineTune(false);
  };

  const handleApplyTrim = () => {
    if (trimMargin === null) return;
    setBusy(true);
    try {
      const removed = jawMesh.applyGumTrimByTeeth(trimMargin);
      toast.success('Gum trimmed along the arch', { description: `${removed} vertices removed` });
      if (fineTune) {
        // The spline belonged to the pre-cut field; back to the offset mode.
        threeInteractionStore.getState().setActiveTool('gum-trim');
        setFineTune(false);
      }
    } catch (error) {
      toast.error('Gum trim failed', {
        description: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setBusy(false);
    }
  };

  const handleContinue = () => {
    jawMesh.disposeGumTrim();
    onContinue();
  };

  return (
    <section className="flex w-full max-w-sm flex-col gap-6 rounded-3xl border border-primary/40 bg-content/80 p-6 text-white shadow-xl shadow-primary/20 backdrop-blur">
      <header className="space-y-3 text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-white/60">Review segmentation</p>
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/15 text-primary">
          <CheckCircle2 className="h-7 w-7" />
        </div>
        <h2 className="text-2xl font-semibold tracking-tight text-white">Review your {jawLabel} jaw</h2>
        <p className="text-sm leading-relaxed text-white/70">
          Inspect the segmented teeth and gum in the viewport. If the result looks wrong, go back to refine the contour
          and run segmentation again.
        </p>
      </header>

      {trimBounds && (
        <div className="rounded-2xl border border-content-highlight/40 bg-content-dark/60 px-4 py-4">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-xs uppercase tracking-[0.3em] text-white/60">Gum trim</span>
            <span className="text-sm font-semibold text-white">
              {trimMargin === null ? '—' : `${trimMargin.toFixed(1)} mm`}
            </span>
          </div>
          <p className="mb-3 text-xs text-white/60">
            {fineTune
              ? 'Drag the spline control points to fine-tune the cut line locally, then apply the trim.'
              : 'Drag on the gum (or use the slider) to offset the cut line from the teeth — the amber curve marks the cut, the red zone gets removed. At 0 mm the curve hugs the tooth necks.'}
          </p>
          <Slider
            value={[trimMargin ?? trimBounds.max]}
            min={trimBounds.min}
            max={trimBounds.max}
            step={0.1}
            disabled={fineTune}
            onValueChange={(value) => handleMarginSlider(value[0] ?? trimBounds.max)}
            className="w-full"
          />
          {fineTune ? (
            <IconButton
              onClick={handleStopFineTune}
              disabled={busy || isContinuePending}
              variant="ghost-light"
              size="sm"
              className="mt-3 w-full rounded-2xl border border-content-highlight/40 bg-content-dark/60 text-xs font-semibold uppercase tracking-[0.3em]"
            >
              Back to offset
            </IconButton>
          ) : (
            <IconButton
              onClick={handleStartFineTune}
              disabled={trimMargin === null || busy || isContinuePending}
              variant="ghost-light"
              size="sm"
              className="mt-3 w-full rounded-2xl border border-content-highlight/40 bg-content-dark/60 text-xs font-semibold uppercase tracking-[0.3em] disabled:opacity-40"
            >
              Fine-tune cut line
            </IconButton>
          )}
          <IconButton
            onClick={handleApplyTrim}
            disabled={trimMargin === null || busy || isContinuePending}
            variant="ghost-primary"
            size="sm"
            className="mt-3 w-full rounded-2xl border border-primary/50 bg-primary/60 text-xs font-semibold uppercase tracking-[0.3em] hover:bg-primary disabled:opacity-40"
          >
            {busy ? 'Trimming…' : 'Apply trim'}
          </IconButton>
        </div>
      )}

      <div className="flex flex-col gap-3">
        <IconButton
          onClick={handleContinue}
          disabled={isContinuePending || busy}
          variant="ghost-primary"
          size="lg"
          className="w-full rounded-2xl border border-primary/50 bg-primary/80 text-sm font-semibold uppercase tracking-[0.25em] hover:bg-primary disabled:opacity-60"
        >
          {isContinuePending
            ? 'Saving…'
            : isLower
              ? 'Continue to upper jaw'
              : 'Save segmentation'}
        </IconButton>

        <IconButton
          onClick={onBackToRefine}
          disabled={isContinuePending || busy}
          variant="ghost-light"
          size="lg"
          icon={ArrowLeft}
          className="w-full rounded-2xl border border-content-highlight/40 bg-content-dark/60 text-sm font-semibold uppercase tracking-[0.25em]"
        >
          Back to refine contour
        </IconButton>
      </div>
    </section>
  );
}
